import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, CATEGORIES, errMsg, PRICING, PRIORITY, STATUS } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const EMPTY = {
  name: "", client_id: "", website_url: "", type: "website", description: "",
  priority: "normal", status: "neu", start_date: "", deadline: "", assigned_to: "none",
  pricing_model: "fixed", price: "", mondher_payment: "", hourly_rate: "", notes: "", next_action: "",
};

export function ProjectForm({ open, onOpenChange, project, clients = [], users = [], onSaved }) {
  const isEdit = !!project;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (project) {
      setForm({
        ...EMPTY,
        ...project,
        assigned_to: project.assigned_to || "none",
        start_date: project.start_date || "",
        deadline: project.deadline || "",
        price: project.price || "",
        mondher_payment: project.mondher_payment || "",
        hourly_rate: project.hourly_rate || "",
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, project]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Projektname ist erforderlich");
    if (!form.client_id) return toast.error("Bitte einen Kunden auswählen");
    setSaving(true);
    const payload = {
      ...form,
      assigned_to: form.assigned_to === "none" ? null : form.assigned_to,
      start_date: form.start_date || null,
      deadline: form.deadline || null,
      price: parseFloat(form.price) || 0,
      mondher_payment: parseFloat(form.mondher_payment) || 0,
      hourly_rate: parseFloat(form.hourly_rate) || 0,
    };
    try {
      const res = isEdit
        ? await api.put(`/projects/${project.id}`, payload)
        : await api.post("/projects", payload);
      toast.success(isEdit ? "Projekt aktualisiert" : "Projekt erstellt");
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-2xl" data-testid="project-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#1D225C]">
            {isEdit ? "Projekt bearbeiten" : "Neues Projekt"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label>Projektname *</Label>
            <Input data-testid="project-form-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="z. B. Website Relaunch" className="mt-1.5" />
          </div>
          <div>
            <Label>Kunde *</Label>
            <Select value={form.client_id} onValueChange={(v) => set("client_id", v)}>
              <SelectTrigger data-testid="project-form-client" className="mt-1.5"><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.company}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Website / Domain</Label>
            <Input data-testid="project-form-website" value={form.website_url} onChange={(e) => set("website_url", e.target.value)} placeholder="beispiel.de" className="mt-1.5" />
          </div>
          <div>
            <Label>Projekttyp</Label>
            <Select value={form.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger data-testid="project-form-type" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(CATEGORIES).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Zugewiesen an</Label>
            <Select value={form.assigned_to} onValueChange={(v) => set("assigned_to", v)}>
              <SelectTrigger data-testid="project-form-assignee" className="mt-1.5"><SelectValue placeholder="Niemand" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Niemand</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Priorität</Label>
            <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger data-testid="project-form-priority" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(PRIORITY).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="project-form-status" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Startdatum</Label>
            <Input data-testid="project-form-start" type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Deadline</Label>
            <Input data-testid="project-form-deadline" type="date" value={form.deadline} onChange={(e) => set("deadline", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Preismodell</Label>
            <Select value={form.pricing_model} onValueChange={(v) => set("pricing_model", v)}>
              <SelectTrigger data-testid="project-form-pricing" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(PRICING).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Kundenpreis (€)</Label>
            <Input data-testid="project-form-price" type="number" min="0" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="1500" className="mt-1.5" />
          </div>
          {form.pricing_model === "hourly" ? (
            <div>
              <Label>Stundensatz Mondher (€)</Label>
              <Input data-testid="project-form-hourly-rate" type="number" min="0" step="0.01" value={form.hourly_rate} onChange={(e) => set("hourly_rate", e.target.value)} placeholder="65" className="mt-1.5" />
            </div>
          ) : (
            <div>
              <Label>Zahlung an Mondher (€)</Label>
              <Input data-testid="project-form-mondher-payment" type="number" min="0" step="0.01" value={form.mondher_payment} onChange={(e) => set("mondher_payment", e.target.value)} placeholder="700" className="mt-1.5" />
            </div>
          )}
          <div className="sm:col-span-2">
            <Label>Beschreibung</Label>
            <Textarea data-testid="project-form-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} placeholder="Was ist zu tun?" className="mt-1.5" />
          </div>
          <div className="sm:col-span-2">
            <Label>Nächster Schritt</Label>
            <Input data-testid="project-form-next-action" value={form.next_action} onChange={(e) => set("next_action", e.target.value)} placeholder="z. B. Feedback vom Kunden abwarten" className="mt-1.5" />
          </div>
          <div className="sm:col-span-2">
            <Label>Interne Notizen</Label>
            <Textarea data-testid="project-form-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="mt-1.5" />
          </div>
        </div>
        <div className="mt-2 flex justify-end gap-2">
          <Button variant="outline" data-testid="project-form-cancel" onClick={() => onOpenChange(false)} className="rounded-xl">
            Abbrechen
          </Button>
          <Button data-testid="project-form-submit" onClick={submit} disabled={saving} className="rounded-xl bg-[#1D225C] text-white hover:bg-[#2a3070]">
            {saving ? "Speichern…" : isEdit ? "Speichern" : "Projekt anlegen"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
